import { GridColDef } from "@mui/x-data-grid-pro";
import {
  CheckIcon,
  GKButton,
  GKCheckbox,
  GKClientDataGrid,
  GKModal,
  Grid2,
  Typography,
} from "components";
import { useMemo } from "react";
import { Controller, useForm, useFormContext } from "react-hook-form";
import toast from "react-hot-toast";
import { usePlaceMFOrderMutation } from "store/features/MFOrders";
import { useAppSelector } from "store/store";
import { extraColumnsField } from "utils/helpers";
import {
  buildPlaceMFOrderPayload,
  getInitialMFOrderFormValues,
  MFOrderFormValues,
} from "./useMFOrders";

interface Props {
  open: boolean;
  setOpen: (open: boolean) => void;
  rationale: string;
  sendForExecution: boolean;
  onSuccess?: () => void;
}

interface DeliveryForm {
  portfolioIds: number[];
  whatsappPortfolios: number[];
}

const OrderConfirmationModal = ({
  open,
  setOpen,
  rationale,
  sendForExecution,
  onSuccess,
}: Props) => {
  const mfForm = useFormContext<MFOrderFormValues>();
  const allocationRows = useAppSelector(
    (state) => state.mfOrders.portfolios,
  ) as any[];

  const [placeMFOrder, { isLoading }] = usePlaceMFOrderMutation();

  const form = useForm<DeliveryForm>({
    defaultValues: { portfolioIds: [], whatsappPortfolios: [] },
  });

  // The order's selected portfolios, enriched with client contact details
  // from the allocation response (held in redux).
  const matchedPortfolios = useMemo(() => {
    const items = mfForm.getValues("items") || [];
    const orderIds = new Set(
      items
        .map((item) => item.portfolioId)
        .filter((id): id is number => id != null),
    );
    return (allocationRows || []).filter((row) =>
      orderIds.has(row?.portfolioId),
    );
  }, [allocationRows, mfForm]);

  // "Send Order Sheet" (sendForExecution=false) uses the order-sheet email;
  // "Send for Execution" uses the client's primary email.
  // const isOrderSheet = !sendForExecution;
  const emailKey = "orderSheetClientEmail";
  // isOrderSheet ? "orderSheetClientEmail" : "clientEmail";

  const validEmailPortfolios = matchedPortfolios.filter(
    (row) => !!row?.[emailKey],
  );
  const validPhonePortfolios = matchedPortfolios.filter(
    (row) => !!row?.clientPhoneNumber,
  );

  const onSubmit = async (values: DeliveryForm) => {
    const payload = buildPlaceMFOrderPayload(
      { ...mfForm.getValues(), rationale },
      {
        sendForExecution,
        portfolioIds: values.portfolioIds,
        whatsappPortfolios: values.whatsappPortfolios,
      },
    );
    try {
      await placeMFOrder(payload).unwrap();
      toast.success(
        sendForExecution ? "Order sent for execution!" : "Order sheet sent!",
      );
      setOpen(false);
      mfForm.reset(getInitialMFOrderFormValues());
      onSuccess?.();
    } catch (err: any) {
      toast.error(err?.data?.message || "Failed to place order");
    }
  };

  const columns: GridColDef[] = [
    {
      ...extraColumnsField(
        `User Name (${matchedPortfolios?.length || 0})`,
        "clientName",
        220,
        1,
      ),
      sortable: false,
      renderCell: (params) => (
        <Grid2
          container
          display="flex"
          flexDirection="column"
          alignItems="flex-start"
        >
          <Typography fontSize={14} fontWeight={600}>
            {params?.row?.clientName}
          </Typography>
          <Typography fontSize={12} color="var(--neutral-500)">
            {params?.row?.portfolioName} • {params?.row?.portfolioCode}
          </Typography>
        </Grid2>
      ),
    },
    {
      ...extraColumnsField("Email", emailKey, 280, 1),
      sortable: false,
      align: "left",
      renderHeader: () => (
        <Grid2 container gap={1} display="flex" alignItems="center">
          <GKCheckbox
            disabled={validEmailPortfolios.length === 0}
            checked={
              validEmailPortfolios.length > 0 &&
              validEmailPortfolios.length === form.watch("portfolioIds").length
            }
            onChange={(e) => {
              e.stopPropagation();
              form.setValue(
                "portfolioIds",
                e.target.checked
                  ? validEmailPortfolios.map((row) => row.portfolioId)
                  : [],
              );
            }}
          />
          <Typography fontWeight={600} fontSize={12}>
            Email
          </Typography>
        </Grid2>
      ),
      renderCell: (params) => (
        <Grid2 container gap={1} display="flex" alignItems="center">
          <Controller
            name="portfolioIds"
            control={form.control}
            render={({ field: { onChange, value } }) => (
              <GKCheckbox
                sx={{ padding: 0 }}
                disabled={!params.row[emailKey]}
                checked={value.includes(params.row.portfolioId)}
                onChange={(e) =>
                  onChange(
                    e.target.checked
                      ? [...value, params.row.portfolioId]
                      : value.filter(
                          (id: number) => id !== params.row.portfolioId,
                        ),
                  )
                }
              />
            )}
          />
          <Typography fontSize={13}>{params.row[emailKey] || "-"}</Typography>
        </Grid2>
      ),
    },
    {
      ...extraColumnsField("Phone No.", "clientPhoneNumber", 180, 1),
      sortable: false,
      renderHeader: () => (
        <Grid2 container gap={1} display="flex" alignItems="center">
          <GKCheckbox
            disabled={validPhonePortfolios.length === 0}
            checked={
              validPhonePortfolios.length > 0 &&
              validPhonePortfolios.length ===
                form.watch("whatsappPortfolios").length
            }
            onChange={(e) => {
              e.stopPropagation();
              form.setValue(
                "whatsappPortfolios",
                e.target.checked
                  ? validPhonePortfolios.map((row) => row.portfolioId)
                  : [],
              );
            }}
          />
          <Typography fontWeight={600} fontSize={12}>
            Phone
          </Typography>
        </Grid2>
      ),
      renderCell: (params) => (
        <Grid2 container gap={1} display="flex" alignItems="center">
          <Controller
            name="whatsappPortfolios"
            control={form.control}
            render={({ field: { onChange, value } }) => (
              <GKCheckbox
                sx={{ padding: 0 }}
                disabled={!params.row.clientPhoneNumber}
                checked={value.includes(params.row.portfolioId)}
                onChange={(e) =>
                  onChange(
                    e.target.checked
                      ? [...value, params.row.portfolioId]
                      : value.filter(
                          (id: number) => id !== params.row.portfolioId,
                        ),
                  )
                }
              />
            )}
          />
          <Typography fontSize={13}>
            {params.row.clientPhoneNumber || "-"}
          </Typography>
        </Grid2>
      ),
    },
  ];

  return (
    <GKModal
      open={open}
      setOpen={setOpen}
      onClose={() => setOpen(false)}
      modalTitle={sendForExecution ? "Send for Execution" : "Send Order Sheet"}
      width="720px"
      footer={
        <Grid2 container justifyContent="flex-end">
          <GKButton
            variant="contained"
            type="submit"
            form="mf-order-confirmation-form"
            startIcon={<CheckIcon height={16} width={16} />}
            loading={isLoading}
          >
            {sendForExecution ? "Send for Execution" : "Send Order Sheet"}
          </GKButton>
        </Grid2>
      }
    >
      <form
        id="mf-order-confirmation-form"
        onSubmit={form.handleSubmit(onSubmit)}
      >
        <GKClientDataGrid
          name="mfOrderConfirmation"
          columns={columns}
          rows={matchedPortfolios.map((row, index) => ({
            ...row,
            id: row?.portfolioId ?? index,
          }))}
          maxHeight="calc(100vh - 355px)"
          showHeaderGrid={false}
          columnsButton={false}
          pagination={false}
        />
      </form>
    </GKModal>
  );
};

export default OrderConfirmationModal;
